import { useEffect } from "react";
import { DatePicker, Form, Input, InputNumber, Modal, Select } from "antd";
import dayjs, { type Dayjs } from "dayjs";

import {
  MILESTONE_TYPES,
  PLAN_COUNTRIES,
  PLAN_SITES,
  type Milestone,
  type MilestoneScope,
  type MilestoneType,
  type MilestoneDraft,
  type MilestoneEdit,
} from "@/views/PlanOfRecord/planOfRecordData";
import type { PlanUser } from "@/views/PlanOfRecord/planAccessData";
import { roleFor } from "@/views/PlanOfRecord/planAccessData";

const SCOPE_OPTIONS: Array<{ value: MilestoneScope; label: string }> = [
  { value: "study", label: "Study-wide" },
  { value: "country", label: "A country" },
  { value: "site", label: "A site" },
];

interface FormShape {
  type: MilestoneType;
  label: string;
  scope: MilestoneScope;
  country?: string;
  site?: string;
  plannedDate: Dayjs;
  actualDate: Dayjs | null;
  atRiskWithinDays?: number;
  rationale: string;
}

/**
 * Add or edit one milestone; rendered only for holders of `plan.edit`. Editing does not
 * offer type or scope — changing what a milestone *is* would rewrite the meaning of its
 * existing audit entries.
 */
export function MilestoneFormModal({
  mode,
  milestone,
  currentUser,
  onCancel,
  onSave,
  onCreate,
}: {
  mode: "create" | "edit" | null;
  milestone: Milestone | null;
  currentUser: PlanUser;
  onCancel: () => void;
  onSave: (id: string, edit: MilestoneEdit, rationale: string) => void;
  onCreate: (draft: MilestoneDraft, rationale: string) => void;
}) {
  const [form] = Form.useForm<FormShape>();
  const scope = Form.useWatch("scope", form);
  const type = Form.useWatch("type", form);

  useEffect(() => {
    if (mode === "edit" && milestone) {
      form.setFieldsValue({
        plannedDate: dayjs(milestone.plannedDate),
        actualDate: milestone.actualDate ? dayjs(milestone.actualDate) : null,
        atRiskWithinDays: milestone.thresholds?.atRiskWithinDays,
        rationale: "",
      });
    }
    if (mode === "create") {
      form.resetFields();
      form.setFieldsValue({ scope: "study", actualDate: null });
    }
  }, [mode, milestone, form]);

  // Offer the type's conventional name so the common cases need no typing.
  useEffect(() => {
    if (mode !== "create" || !type) return;
    const suggested = MILESTONE_TYPES.find((entry) => entry.value === type);
    if (suggested && suggested.suggestedLabel) {
      form.setFieldsValue({ label: suggested.suggestedLabel });
    }
  }, [type, mode, form]);

  const isCreate = mode === "create";

  return (
    <Modal
      open={mode !== null}
      title={isCreate ? "Add a milestone" : `Edit — ${milestone?.label ?? ""}`}
      okText={isCreate ? "Add milestone" : "Save change"}
      width={580}
      onCancel={onCancel}
      onOk={async () => {
        let values: FormShape;
        try {
          values = await form.validateFields();
        } catch {
          // Validation messages are already rendered against the fields.
          return;
        }

        const plannedDate = values.plannedDate.format("YYYY-MM-DD");
        const actualDate = values.actualDate
          ? values.actualDate.format("YYYY-MM-DD")
          : null;
        const thresholds =
          values.atRiskWithinDays === undefined ||
          values.atRiskWithinDays === null
            ? null
            : { atRiskWithinDays: values.atRiskWithinDays };

        if (isCreate) {
          const site = PLAN_SITES.find(
            (candidate) => candidate.siteNumber === values.site,
          );
          const scopeLabel =
            values.scope === "study"
              ? "Study-wide"
              : values.scope === "country"
                ? (values.country ?? "")
                : site
                  ? `Site ${site.siteNumber} · ${site.siteName}`
                  : "";

          onCreate(
            {
              type: values.type,
              label: values.label,
              scope: values.scope,
              scopeLabel,
              plannedDate,
              actualDate,
              thresholds,
              countryName:
                values.scope === "country" ? values.country : undefined,
              siteNumber: values.scope === "site" ? values.site : undefined,
            },
            values.rationale,
          );
          return;
        }

        if (!milestone) return;
        onSave(
          milestone.id,
          { plannedDate, actualDate, thresholds },
          values.rationale,
        );
      }}
      destroyOnHidden
    >
      <p className="mb-4 text-[12px] text-[#6B7280]">
        Recorded against{" "}
        <span className="font-semibold text-[#374151]">{currentUser.name}</span>{" "}
        · {roleFor(currentUser).name}
      </p>

      <Form form={form} layout="vertical" requiredMark={false}>
        {isCreate ? (
          <>
            <Form.Item
              name="type"
              label="Milestone type"
              rules={[{ required: true, message: "Pick a milestone type." }]}
            >
              <Select
                placeholder="Select a type"
                options={MILESTONE_TYPES.map((entry) => ({
                  value: entry.value,
                  label: entry.label,
                }))}
              />
            </Form.Item>

            <Form.Item
              name="label"
              label="Name"
              rules={[
                { required: true, message: "Give the milestone a name." },
              ]}
            >
              <Input placeholder="e.g. 50% sites activated" />
            </Form.Item>

            <Form.Item name="scope" label="Scope">
              <Select options={SCOPE_OPTIONS} />
            </Form.Item>

            {scope === "country" ? (
              <Form.Item
                name="country"
                label="Country"
                rules={[{ required: true, message: "Pick a country." }]}
              >
                <Select
                  placeholder="Select a country"
                  options={PLAN_COUNTRIES.map((country) => ({
                    value: country,
                    label: country,
                  }))}
                />
              </Form.Item>
            ) : null}

            {scope === "site" ? (
              <Form.Item
                name="site"
                label="Site"
                rules={[{ required: true, message: "Pick a site." }]}
              >
                <Select
                  placeholder="Select a site"
                  options={PLAN_SITES.map((site) => ({
                    value: site.siteNumber,
                    label: `${site.siteNumber} · ${site.siteName}`,
                  }))}
                />
              </Form.Item>
            ) : null}
          </>
        ) : null}

        <Form.Item
          name="plannedDate"
          label="Planned date"
          rules={[{ required: true, message: "A planned date is required." }]}
        >
          <DatePicker className="w-full" format="YYYY-MM-DD" />
        </Form.Item>

        {!isCreate ? (
          <Form.Item
            name="actualDate"
            label="Actual date"
            extra="Set this once the milestone has actually happened — it's what marks the milestone complete."
          >
            <DatePicker className="w-full" format="YYYY-MM-DD" allowClear />
          </Form.Item>
        ) : null}

        <Form.Item
          name="atRiskWithinDays"
          label="Flag at risk within (optional)"
          extra="Left empty, this milestone never reads at risk. It still reads late once its planned date passes."
        >
          <InputNumber className="w-full" min={0} max={365} addonAfter="days" />
        </Form.Item>

        <Form.Item
          name="rationale"
          label={
            isCreate ? "Why are you adding this?" : "Why is this changing?"
          }
          rules={[
            {
              required: true,
              message: "A rationale is required for every plan change.",
            },
            {
              min: 10,
              message: "Give enough detail to be useful in six months.",
            },
          ]}
        >
          <Input.TextArea
            rows={3}
            placeholder={
              isCreate
                ? "e.g. Sponsor-owned activation checkpoint agreed at the Aug governance meeting."
                : "e.g. Site 118 contract execution slipped; activation re-planned to end of Q4."
            }
          />
        </Form.Item>
      </Form>
    </Modal>
  );
}
